"use client";

import { useState, useEffect } from "react";
import { Eye, EyeOff, Lock, Mail, Clock } from "lucide-react";
import { setSession, clearSession } from "@/lib/auth";
import Link from "next/link";

/** Only same-origin paths — never "//host" or "/\\host", which browsers treat as another site. */
function safeNextPath(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "/";
  if (raw.startsWith("/login")) return "/";
  return raw;
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const [expired, setExpired] = useState(false);

  // Arrived here because the session ended — drop the stale local copy and say why.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired") === "1") {
      clearSession();
      setExpired(true);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const resolveLocation = (): Promise<string> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve("");
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await res.json();
            const city =
              data.address?.city || data.address?.town ||
              data.address?.village || data.address?.county || "";
            const country = data.address?.country || "";
            resolve(city && country ? `${city}, ${country}` : city || country || "");
          } catch { resolve(""); }
        },
        () => resolve(""),
        { timeout: 5000 }
      );
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Invalid email or password.");
        setLoading(false);
        return;
      }
      setSession({ staffId: data.staffId, email: data.email, role: data.role, name: data.name });
      const location = await resolveLocation();
      if (location) {
        fetch(`/api/staff/${data.staffId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ location }),
        }).catch(() => {});
      }
      window.location.href = safeNextPath(new URLSearchParams(window.location.search).get("next"));
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <>
      <style>{`
        /* ── Left panel ── */
        @keyframes deepBluePulse {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .gradient-bg {
          background: linear-gradient(-45deg, #0d1b2a, #0a1628, #1b2d45, #0f2a40, #0d1b2a);
          background-size: 400% 400%;
          animation: deepBluePulse 14s ease infinite;
        }

        @keyframes waveMove1 {
          0% { transform: translateX(0) translateY(0) scaleY(1); }
          50% { transform: translateX(-60px) translateY(-18px) scaleY(1.08); }
          100% { transform: translateX(0) translateY(0) scaleY(1); }
        }
        @keyframes waveMove2 {
          0% { transform: translateX(0) translateY(0) scaleY(1); }
          50% { transform: translateX(50px) translateY(12px) scaleY(0.94); }
          100% { transform: translateX(0) translateY(0) scaleY(1); }
        }
        @keyframes waveMove3 {
          0% { transform: translateX(0) translateY(0); }
          33% { transform: translateX(40px) translateY(-10px); }
          66% { transform: translateX(-30px) translateY(8px); }
          100% { transform: translateX(0) translateY(0); }
        }
        .wave1 { animation: waveMove1 18s ease-in-out infinite; }
        .wave2 { animation: waveMove2 22s ease-in-out infinite; }
        .wave3 { animation: waveMove3 15s ease-in-out infinite; }

        @keyframes shimmer {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes osShift {
          0%, 100% {
            color: #89b4fa;
            text-shadow: 0 0 10px rgba(137,180,250,0.45), 0 0 24px rgba(137,180,250,0.15);
          }
          50% {
            color: #b8d4fd;
            text-shadow: 0 0 16px rgba(184,212,253,0.7), 0 0 36px rgba(137,180,250,0.3);
          }
        }
        .os-word {
          font-weight: 700;
          letter-spacing: 0.12em;
          animation: osShift 4s ease-in-out infinite;
        }

        /* Entrance animations */
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes welcomePop {
          0% { opacity: 0; transform: translateY(-16px) scale(0.96); }
          60% { opacity: 1; transform: translateY(4px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        .animate-panel { animation: slideLeft 0.9s cubic-bezier(0.22,1,0.36,1) both; }
        .animate-welcome { animation: welcomePop 0.8s cubic-bezier(0.22,1,0.36,1) 0.2s both; }
        .animate-subtitle { animation: fadeIn 0.6s ease 0.55s both; }
        .animate-card { animation: slideUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.35s both; }
        .animate-field1 { animation: slideUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.5s both; }
        .animate-field2 { animation: slideUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.6s both; }
        .animate-field3 { animation: slideUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.7s both; }
        .animate-btn { animation: slideUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.78s both; }
        .animate-footer { animation: fadeIn 0.5s ease 1.1s both; }

        /* Input */
        .input-field { transition: all 0.2s ease; }
        .input-field:focus {
          border-color: #89b4fa !important;
          box-shadow: 0 0 0 3px rgba(137,180,250,0.15);
          background: #3b3b52 !important;
        }
        .input-field::placeholder { color: #585b70; }

        /* Button */
        @keyframes btnPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(137,180,250,0), 0 4px 16px rgba(0,0,0,0.4); }
          50% { box-shadow: 0 0 0 4px rgba(137,180,250,0.08), 0 4px 16px rgba(0,0,0,0.4); }
        }
        @keyframes btnShimmer {
          0% { left: -60%; }
          100% { left: 120%; }
        }
        .sign-in-btn {
          background: linear-gradient(135deg, #89b4fa 0%, #5a8fd4 40%, #3a6bab 100%);
          border: none;
          box-shadow: 0 4px 18px rgba(137,180,250,0.3), 0 1px 4px rgba(0,0,0,0.3);
          transition: all 0.25s cubic-bezier(0.22,1,0.36,1);
          position: relative;
          overflow: hidden;
          animation: btnPulse 3s ease-in-out infinite;
        }
        .sign-in-btn::after {
          content: '';
          position: absolute;
          top: -20%;
          left: -60%;
          width: 30%;
          height: 140%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          transform: skewX(-15deg);
          animation: btnShimmer 3s ease-in-out infinite 0.8s;
          pointer-events: none;
        }
        .sign-in-btn:hover {
          background: linear-gradient(135deg, #9dc4fb 0%, #6a9fe4 40%, #4a7bbb 100%);
          box-shadow: 0 6px 24px rgba(137,180,250,0.45), 0 2px 8px rgba(0,0,0,0.3);
          transform: translateY(-2px);
          animation: none;
        }
        .sign-in-btn:active {
          transform: translateY(1px);
          box-shadow: 0 2px 8px rgba(137,180,250,0.2), 0 1px 3px rgba(0,0,0,0.3);
          animation: none;
        }
        .sign-in-btn > * { position: relative; z-index: 1; }

        /* Grid overlay */
        .grid-overlay {
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 56px 56px;
        }

        /* Right panel */
        .right-bg {
          background-color: #181825;
          background-image:
            radial-gradient(ellipse at 30% 20%, rgba(137,180,250,0.05) 0%, transparent 55%),
            radial-gradient(ellipse at 75% 80%, rgba(90,111,133,0.06) 0%, transparent 50%);
        }
      `}</style>

      <div className="min-h-screen flex">

        {/* ══ LEFT PANEL ══ */}
        <div className="hidden lg:flex lg:w-[58%] gradient-bg relative overflow-hidden flex-col items-center justify-center p-16">
          <div className="absolute inset-0 grid-overlay" />

          {/* Wave blobs */}
          <div className="wave1 absolute -bottom-32 -left-24 w-[700px] h-[400px] rounded-[60%_40%_40%_60%/60%_60%_40%_40%]"
            style={{ background: "radial-gradient(ellipse, rgba(29,78,216,0.22) 0%, transparent 70%)" }} />
          <div className="wave2 absolute -top-40 -right-32 w-[600px] h-[500px] rounded-[40%_60%_60%_40%/40%_40%_60%_60%]"
            style={{ background: "radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)" }} />
          <div className="wave3 absolute top-[35%] left-[15%] w-[450px] h-[300px] rounded-[50%]"
            style={{ background: "radial-gradient(ellipse, rgba(96,165,250,0.1) 0%, transparent 70%)" }} />

          {/* Subtle vertical line accent */}
          <div className="absolute left-12 top-0 bottom-0 w-px" style={{ background: "linear-gradient(to bottom, transparent, rgba(137,180,250,0.1) 30%, rgba(137,180,250,0.1) 70%, transparent)" }} />

          {/* Content */}
          <div className={`relative z-10 max-w-lg ${mounted ? "animate-panel" : "opacity-0"}`}>

            <p className="text-xs font-semibold tracking-[0.25em] uppercase mb-6" style={{ color: "#89b4fa" }}>
              Serviced accommodation, engineered
            </p>

            <h2 className="text-5xl font-bold leading-[1.15] mb-6 tracking-tight" style={{ color: "#cdd6f4" }}>
              Efficiency isn&apos;t a feature — <span style={{ color: "#a6adc8" }}>it&apos;s the standard.</span>
            </h2>

            <p className="text-base leading-relaxed mb-10" style={{ color: "#7f849c" }}>
              Built by operators who&apos;ve run the properties, managed the teams, and felt every gap in the process. This is the system we wished existed.
            </p>

            {/* Feature rows — clean line list */}
            <div className="space-y-0">
              {[
                { label: "Properties & Listings",  note: "Centralised portfolio management" },
                { label: "Finance & Statements",    note: "Real-time owner reporting" },
                { label: "Scheduling & Tasks",      note: "Team coordination at scale" },
                { label: "Reviews & Reputation",    note: "Guest feedback pipeline" },
              ].map((item, i) => (
                <div key={item.label}
                  className="flex items-center justify-between py-3.5 border-b"
                  style={{
                    borderColor: "#3b3b52",
                    animation: mounted ? `slideUp 0.45s cubic-bezier(0.22,1,0.36,1) ${0.7 + i * 0.07}s both` : "none",
                    opacity: mounted ? undefined : 0,
                  }}>
                  <span className="text-sm font-medium" style={{ color: "#cdd6f4" }}>{item.label}</span>
                  <span className="text-xs" style={{ color: "#585b70" }}>{item.note}</span>
                </div>
              ))}
            </div>

            {/* Enquiry CTA */}
            <a href="mailto:hello@staycasa.co.uk?subject=Cloud OS Enquiry"
              className="inline-flex items-center gap-2 mt-10 transition-all group"
              style={{ color: "#4a5568" }}
              onMouseEnter={e => (e.currentTarget.style.color = "#89b4fa")}
              onMouseLeave={e => (e.currentTarget.style.color = "#4a5568")}>
              <span style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }} className="uppercase">Want something similar?</span>
              <span className="text-xs transition-transform group-hover:translate-x-1">Enquire →</span>
            </a>
          </div>

          {/* Bottom version */}
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <p className="text-xs tracking-widest" style={{ color: "#7f849c" }}>Cloud OS v2.0.5</p>
          </div>
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className="flex-1 flex flex-col items-center justify-center right-bg relative overflow-hidden px-6 py-12">

          {/* Staycasa Cloud OS — top hierarchy, centred, animated */}
          <div className={`mb-10 ${mounted ? "animate-welcome" : "opacity-0"}`}>
            <div className="flex flex-col items-center">
              <span className="text-4xl font-bold tracking-tight leading-tight" style={{ color: "#cdd6f4" }}>Staycasa</span>
              <div className="flex items-baseline gap-3 leading-tight">
                <span className="text-4xl font-bold tracking-tight" style={{ color: "#cdd6f4" }}>Cloud</span>
                <span className="text-4xl os-word">OS</span>
              </div>
            </div>
          </div>

          {/* Welcome sub-heading + subtitle */}
          <div className={`text-center mb-6 ${mounted ? "animate-subtitle" : "opacity-0"}`}>
            <p className="text-base font-medium" style={{ color: "#a6adc8" }}>Welcome back</p>
            <p className="text-sm mt-1" style={{ color: "#7f849c" }}>Sign in to continue</p>
          </div>

          {/* Card */}
          <div className={`w-full max-w-[400px] ${mounted ? "animate-card" : "opacity-0"}`}>
            <div className="rounded-2xl border p-8" style={{ background: "#252535", borderColor: "#3b3b52" }}>
              <form onSubmit={handleSubmit} className="space-y-5">

                {/* Email */}
                <div className={mounted ? "animate-field1" : "opacity-0"}>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#7f849c" }}>
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#585b70" }} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@staycasa.co.uk"
                      required
                      className="input-field w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none"
                      style={{ background: "#313244", border: "1px solid #3b3b52", color: "#cdd6f4" }}
                    />
                  </div>
                </div>

                {/* Password */}
                <div className={mounted ? "animate-field2" : "opacity-0"}>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#7f849c" }}>
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#585b70" }} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="input-field w-full pl-10 pr-11 py-3 rounded-xl text-sm focus:outline-none"
                      style={{ background: "#313244", border: "1px solid #3b3b52", color: "#cdd6f4" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: "#585b70" }}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Remember + Forgot */}
                <div className={`flex items-center justify-between ${mounted ? "animate-field3" : "opacity-0"}`}>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: "#7f849c" }}>
                    <input type="checkbox" className="rounded" style={{ accentColor: "#89b4fa" }} />
                    Remember me
                  </label>
                  <Link href="/forgot-password" className="text-sm font-medium transition-colors" style={{ color: "#89b4fa" }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#b4d0fa")}
                    onMouseLeave={e => (e.currentTarget.style.color = "#89b4fa")}>
                    Forgot password?
                  </Link>
                </div>

                {/* Session expired notice */}
                {expired && !error && (
                  <div className="rounded-xl px-4 py-3 text-sm flex items-start gap-2" style={{ background: "color-mix(in srgb, var(--color-primary) 15%, transparent)", border: "1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)", color: "color-mix(in srgb, var(--color-primary) 60%, white)" }}>
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" /> Your session has expired. Please sign in again.
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="rounded-xl px-4 py-3 text-sm flex items-start gap-2" style={{ background: "rgba(153,27,27,0.2)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5" }}>
                    <span className="mt-0.5 shrink-0">⚠</span> {error}
                  </div>
                )}

                {/* Submit */}
                <div className={mounted ? "animate-btn" : "opacity-0"}>
                  <button
                    type="submit"
                    disabled={loading}
                    className="sign-in-btn w-full text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                </div>
              </form>
            </div>

            <p className={`text-center text-xs mt-6 ${mounted ? "animate-footer" : "opacity-0"}`} style={{ color: "#585b70" }}>
              For access issues, contact your system administrator.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
